import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TuteePrivateRequests.css';

// ── API Base URL ───────────────────────────────────────────
const API_BASE = 'https://mykaddle-api.onrender.com';

const TuteePrivateRequests = () => {
  const navigate = useNavigate();

  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [finishedRequests, setFinishedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── FETCH REQUESTS ON MOUNT ──
  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(
        `${API_BASE}/api/tutee/requests/history?type=tutee`,
        { headers }
      );

      const allRequests = res.data.requests || res.data || [];

      const pending = allRequests.filter(r => 
        r.status === 'pending' || r.status === 'sent'
      );
      
      const accepted = allRequests.filter(r => 
        (r.status === 'accepted' || r.status === 'active') && 
        !r.isFinished && 
        !r.finishedAt
      );
      
      const finished = allRequests.filter(r => 
        r.isFinished === true || 
        r.status === 'finished' || 
        r.status === 'completed' ||
        r.finishedAt
      );

      setPendingRequests(pending);
      setAcceptedRequests(accepted);
      setFinishedRequests(finished);
      
    } catch (err) {
      console.error('Error fetching requests:', err);
      setError(err.response?.data?.error || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  // ── FORMAT DATE ──
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // ── FORMAT DATE RANGE ──
  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const weeks = Math.ceil(diffDays / 7);

    return `${start.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short'
    })} - ${end.toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short'
    })}, ${weeks} week${weeks > 1 ? 's' : ''}`;
  };

  // ── OPEN CHAT ──
  const handleOpenChat = async (request) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('🔍 Opening chat for request:', { requestId: request._id, counterpartyName: request.counterpartyName });
      
      // First try groupSubjectId from history
      if (request.groupSubjectId) {
        console.log('🔗 Using groupSubjectId:', request.groupSubjectId);
        navigate(`/request-portal/${request.groupSubjectId}`);
        return;
      }
      
      // Fallback: look up active groups
      const res = await axios.get(`${API_BASE}/api/tutee/requests/active-groups`, { headers });
      const groups = res.data.groups || [];
      
      console.log('📋 Active groups:', groups.map(g => ({ 
        subjectId: g.subjectId, 
        requestId: g.requestId,
        status: g.status 
      })));
      
      // Find the group that matches this request
      const matchingGroup = groups.find(g => g.requestId === request._id);
      
      if (matchingGroup) {
        console.log('✅ Found matching group:', matchingGroup.subjectId);
        navigate(`/request-portal/${matchingGroup.subjectId}`);
      } else {
        // Last resort: use request _id
        console.log('⚠️ No matching group, using request _id:', request._id);
        navigate(`/request-portal/${request._id}`);
      }
    } catch (err) {
      console.error('Failed to find group:', err);
      // Last resort: use request _id
      console.log('⚠️ Error fallback - using request _id:', request._id);
      navigate(`/request-portal/${request._id}`);
    }
  };

  // ── MARK AS FINISHED ──
  const handleMarkFinished = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/tutee/requests/${requestId}/finish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchRequests();
    } catch (err) {
      console.error('Error marking request as finished:', err);
      alert(err.response?.data?.error || 'Failed to update request');
    }
  };

  // ── RENDER ──
  if (loading) {
    return (
      <div className="tppr-dashboard-container">
        <div className="tppr-almighty-wrapper">
          <div className="tppr-empty-state-container">
            <p className="tppr-empty-state-text">Loading requests...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tppr-dashboard-container">
        <div className="tppr-almighty-wrapper">
          <div className="tppr-empty-state-container">
            <p className="tppr-empty-state-text" style={{ color: '#d64045' }}>
              {error}
            </p>
            <button
              className="tppr-req-received"
              onClick={fetchRequests}
              style={{ cursor: 'pointer', marginTop: '0.5rem' }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const hasRequests = [...acceptedRequests, ...pendingRequests, ...finishedRequests].length > 0;

  return (
    <div className="tppr-dashboard-container">
      <div className="tppr-almighty-wrapper">

        {/* Top Bar */}
        <div className="tppr-top-bar">
          <div className="tppr-top-left-header">
            <div className="tppr-header-row">
              <button
                className="tppr-back-icon-btn"
                aria-label="Go back"
                onClick={() => navigate(-1)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="0.5em" height="1em" viewBox="0 0 12 24">
                  <path d="M0 0h12v24H0z" fill="none" />
                  <path fill="currentColor" fillRule="evenodd" d="M10 19.438L8.955 20.5l-7.666-7.79a1.02 1.02 0 0 1 0-1.42L8.955 3.5L10 4.563L2.682 12z" />
                </svg>
              </button>
              <span className="tppr-page-title">Private Requests</span>
            </div>
            <p className="tppr-sub-header">
              View and manage your private tutoring requests. Track accepted, pending, and finished requests all in one place.
            </p>
          </div>
        </div>

       
        {/* Scrollable Cards Area */}
        <div className="tppr-cards-scroll-area">
          {!hasRequests ? (
            <div className="tppr-empty-state-container">
              <p className="tppr-empty-state-text">No requests. Go to discover to make them.</p>
            </div>
          ) : (
            <>
              {/* ACCEPTED REQUESTS */}
              {acceptedRequests.map((req) => (
                <div key={req._id} className="tppr-req-accepted">
                  <div className="tppr-req-name-row">
                    <span className="tppr-req-name">{req.counterpartyName}</span>
                    <span className="tppr-accepted-badge">Accepted</span>
                  </div>
                  <div className="tppr-req-subject">{req.subjectDisplayName}</div>
                  <div className="tppr-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="tppr-req-duration-text">Accepted on {formatDate(req.respondedAt)}</span>
                  </div>
                  <div className="tppr-req-btns">
                    <span className="tppr-status-in-progress">In Progress</span>
                    <button className="tppr-req-open-chat" onClick={() => handleOpenChat(req)}>
                      Open Chat
                    </button>
                  </div>
                </div>
              ))}

              {/* PENDING REQUESTS */}
              {pendingRequests.map((req) => (
                <div key={req._id} className="tppr-req1">
                  <div className="tppr-req-name">{req.counterpartyName}</div>
                  <div className="tppr-req-msg">
                    {req.helpText || 'No description provided.'}
                  </div>
                  <div className="tppr-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="tppr-req-duration-text">
                      Sent on {formatDate(req.createdAt)}
                    </span>
                  </div>
                  <div className="tppr-req-btns">
                    <span className="tppr-req-received">Request Sent</span>
                  </div>
                </div>
              ))}

              {/* FINISHED REQUESTS */}
              {finishedRequests.map((req) => (
                <div key={req._id} className="tppr-req-accepted">
                  <div className="tppr-req-name-row">
                    <span className="tppr-req-name">{req.counterpartyName}</span>
                    <span className="tppr-finished-badge">Finished</span>
                  </div>
                  <div className="tppr-req-subject">{req.subjectDisplayName}</div>
                  <div className="tppr-req-duration">
                    <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                      <path d="M0 0h24v24H0z" fill="none" />
                      <g fill="none" stroke="currentColor" strokeLinejoin="round" strokeWidth="1.5">
                        <path strokeLinecap="round" d="m4.095 3.298l15.81-.048m-15.81 17.5l15.81-.048" />
                        <path d="M18.426 3.31H5.574l.079 1.449a7.38 7.38 0 0 0 2.251 4.913l1.242 1.195a1.58 1.58 0 0 1 0 2.279L7.904 14.34a7.38 7.38 0 0 0-2.251 4.913l-.08 1.448h12.853l-.079-1.445a7.38 7.38 0 0 0-2.256-4.917l-1.242-1.194a1.58 1.58 0 0 1 0-2.28l1.242-1.193a7.38 7.38 0 0 0 2.256-4.918z" />
                      </g>
                    </svg>
                    <span className="tppr-req-duration-text">
                      Finished on {formatDate(req.finishedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Info Bar */}
        <div className="tppr-info-bar">
          <div className="tppr-info-bar-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
              <path d="M0 0h24v24H0z" fill="none" />
              <path fill="#f3f800" d="M12 2.5c-3.81 0-6.5 2.743-6.5 6.119c0 1.536.632 2.572 1.425 3.56c.172.215.347.422.527.635l.096.112c.21.25.427.508.63.774c.404.531.783 1.128.995 1.834a.75.75 0 0 1-1.436.432c-.138-.46-.397-.89-.753-1.357a18 18 0 0 0-.582-.714l-.092-.11c-.18-.212-.37-.436-.555-.667C4.87 12.016 4 10.651 4 8.618C4 4.363 7.415 1 12 1s8 3.362 8 7.619c0 2.032-.87 3.397-1.755 4.5c-.185.23-.375.454-.555.667l-.092.109c-.21.248-.405.481-.582.714c-.356.467-.615.898-.753 1.357a.751.751 0 0 1-1.437-.432c.213-.706.592-1.303.997-1.834c.202-.266.419-.524.63-.774l.095-.112c.18-.213.355-.42.527-.634c.793-.99 1.425-2.025 1.425-3.561C18.5 5.243 15.81 2.5 12 2.5M8.75 18h6.5a.75.75 0 0 1 0 1.5h-6.5a.75.75 0 0 1 0-1.5m.75 3.75a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75" />
            </svg>
          </div>
          <p className="tppr-info-bar-text">
            The duration set on every request you make does not necessarily dictate how long your session should be with your tutor. You can always end it sooner or later than the expected time on your own accord. <strong>For more information, visit support.</strong>
          </p>
        </div>

      </div>
    </div>
  );
};

export default TuteePrivateRequests;